export type MediaType = 'movie' | 'tv' | 'anime';

export interface MovieDetails {
  type: 'movie';
  tmdbId: number;
  title: string;
  overview: string;
  posterPath: string | null;
  backdropPath: string | null;
  runtimeMinutes: number | null;
  releaseDate: string | null;
}

export interface TvDetails {
  type: 'tv';
  tmdbId: number;
  title: string;
  overview: string;
  posterPath: string | null;
  backdropPath: string | null;
  firstAirDate: string | null;
  seasons: Array<{ seasonNumber: number; episodeCount: number; name: string }>;
}

export type MediaDetails = MovieDetails | TvDetails;

export interface AnimeDetails {
  type: 'anime';
  malId: number;
  title: string;
  overview: string | null;
  posterUrl: string | null;
  backdropUrl: string | null;
  episodes: number | null;
  airing: boolean;
  voteAverage: number | null;
  defaultAudio: 'sub' | 'dub';
  genres: string[];
  releaseDate: string | null;
}

export interface EpisodeSummary {
  seasonNumber: number;
  episodeNumber: number;
  name: string;
  overview: string;
  stillPath: string | null;
  airDate: string | null;
}

export interface TrackEntry {
  lang: string;
  label: string;
  url: string;
  kind: 'subtitles' | 'captions';
}

export type StreamRef =
  | { type: 'movie'; tmdbId: number }
  | { type: 'tv'; tmdbId: number; season: number; episode: number }
  | { type: 'anime'; malId: number; episode: number; audio: 'sub' | 'dub' };

/**
 * Allow-list of source provider names. Must stay in sync with the backend's
 * SOURCE_NAMES in `cobstream-backend/src/services/streaming/streaming.types.ts`
 * and the `app.ts` providers map. Drift fails non-fatally: missing-on-backend
 * → 400 BAD_SOURCE on preflight/playlist; missing-on-frontend → the new
 * provider is invisible in the SourceMenu.
 */
export const SOURCE_NAMES = [
  'vidlink',
  'vidzee',
  'vidrock',
  'moviesapi',
  'moviesapi-vip',
  'videasy',
  'flickygate',
  'purstream',
  'klikxxi',
  'hollymoviehd',
  'animekai-sub',
  'animekai-dub',
  'miruro-sub',
  'miruro-dub',
  'animepahe-sub',
  'animepahe-dub',
  'vaplayer',
  'aether',
  'ridomovies',
  'vixsrc',
  'showbox',
] as const;
export type SourceName = (typeof SOURCE_NAMES)[number];

export function isKnownSourceName(value: unknown): value is SourceName {
  return typeof value === 'string' && (SOURCE_NAMES as readonly string[]).includes(value);
}

export type PreflightKind =
  | 'not_supported'
  | 'upstream_4xx'
  | 'upstream_5xx'
  | 'parse_failed'
  | 'decrypt_failed'
  | 'timeout'
  | 'unknown'
  | 'playlist_http';

export interface PreflightResult {
  ok: boolean;
  providerName: SourceName;
  kind?: PreflightKind;
  message?: string;
}
